import {
  SECTION_IDS,
  resolveEffectiveSectionInsights,
  sectionSupportsInsightCards,
  type ReportSectionDto,
  type SectionId,
} from '@act/shared/data-transfer-objects';
import {
  EMPTY_REPORT_INSIGHT_CARD,
  ReportInsightPair,
  toReportInsightCardData,
  type ReportInsightCardKey,
  type ReportInsightFieldKey,
} from './ReportInsightPair';

export interface ReportSectionInsightBlocksProps {
  sectionId: string;
  dataSnapshot: Record<string, unknown> | null;
  sectionInsights: ReportSectionDto['insights'];
  clinicalInsightEnabled: boolean;
  financialInsightEnabled: boolean;
  /** Builder preview: edit insight copy inline. */
  editable?: boolean;
  /**
   * @param card - Clinical or financial insight card.
   * @param field - Overline, heading, or body field.
   * @param value - Updated text while typing.
   */
  onInsightFieldChange?: (
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
  /**
   * @param card - Clinical or financial insight card.
   * @param field - Overline, heading, or body field.
   * @param value - Final text after blur.
   */
  onInsightFieldBlur?: (
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
}

/**
 * Renders optional clinical/financial insight cards after the section callout.
 * @param props - Section id, snapshot data, and per-row insight toggles.
 * @returns Tan insight band markup or null when insights are disabled or absent.
 */
export const ReportSectionInsightBlocks = ({
  sectionId,
  dataSnapshot,
  sectionInsights,
  clinicalInsightEnabled,
  financialInsightEnabled,
  editable = false,
  onInsightFieldChange,
  onInsightFieldBlur,
}: ReportSectionInsightBlocksProps) => {
  const isSectionId = (id: string): id is SectionId =>
    (SECTION_IDS as readonly string[]).includes(id);

  if (!isSectionId(sectionId) || !sectionSupportsInsightCards(sectionId)) {
    return null;
  }

  const effective = resolveEffectiveSectionInsights(
    sectionInsights,
    dataSnapshot,
  );
  const clinical = clinicalInsightEnabled
    ? (toReportInsightCardData(effective?.clinical) ??
        (editable ? EMPTY_REPORT_INSIGHT_CARD : null))
    : null;
  const financial = financialInsightEnabled
    ? (toReportInsightCardData(effective?.financial) ??
        (editable ? EMPTY_REPORT_INSIGHT_CARD : null))
    : null;

  if (!clinical && !financial) {
    return null;
  }

  return (
    <section className="report-section-insight-band">
      <div className="container">
        <ReportInsightPair
          clinical={clinical}
          financial={financial}
          editable={editable}
          onFieldChange={onInsightFieldChange}
          onFieldBlur={onInsightFieldBlur}
        />
      </div>
    </section>
  );
};
